import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-all duration-300 ease-out disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent-gold))] focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // Primary - solid dark with subtle gold accent on hover
        default:
          "bg-primary text-primary-foreground rounded-md border border-transparent hover:border-[hsl(var(--accent-gold)/0.3)] shadow-sm hover:shadow-md hover:shadow-[hsl(var(--accent-gold)/0.08)] hover:-translate-y-px active:translate-y-0",

        // Destructive - refined red
        destructive:
          "bg-destructive text-destructive-foreground rounded-md shadow-sm hover:bg-destructive/90 hover:shadow-md active:shadow-sm",

        // Outline - sophisticated border with subtle fill on hover
        outline:
          "border border-border bg-transparent rounded-md hover:bg-secondary/50 hover:border-[hsl(var(--accent-gold)/0.3)] text-foreground dark:hover:bg-secondary/30",

        // Secondary - muted background
        secondary:
          "bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/80 shadow-sm",

        // Ghost - minimal, just text
        ghost:
          "text-muted-foreground hover:text-foreground hover:bg-secondary/50 rounded-md dark:hover:bg-secondary/30",

        // Link - underline style
        link: "text-foreground underline-offset-4 hover:underline decoration-[hsl(var(--accent-gold))]",

        // Premium - gold accent button for CTAs
        premium:
          "bg-[hsl(var(--accent-gold))] text-[hsl(var(--accent-gold-foreground))] rounded-md font-semibold shadow-md shadow-[hsl(var(--accent-gold)/0.25)] hover:shadow-lg hover:shadow-[hsl(var(--accent-gold)/0.35)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-md border border-[hsl(var(--accent-gold))]",

        // Glow - legacy support, now maps to premium style with gradient
        glow: "bg-gradient-to-r from-[hsl(var(--accent-gold))] via-[hsl(38,85%,55%)] to-[hsl(var(--accent-gold))] text-[hsl(var(--accent-gold-foreground))] rounded-md font-semibold shadow-md shadow-[hsl(var(--accent-gold)/0.25)] hover:shadow-lg hover:shadow-[hsl(var(--accent-gold)/0.4)] hover:-translate-y-0.5 active:translate-y-0 border-0",
      },
      size: {
        default: "h-10 px-5 py-2 has-[>svg]:px-3",
        sm: "h-9 rounded-md gap-1.5 px-4 text-xs has-[>svg]:px-2.5",
        lg: "h-12 rounded-md px-8 has-[>svg]:px-4 text-base",
        xl: "h-14 rounded-lg px-10 has-[>svg]:px-5 text-base",
        icon: "size-10 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
